import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trophy, Target, Star, Calendar, BookOpen } from 'lucide-react';
import { playerService } from '../services/playerService';
import { useToast } from '../hooks/use-toast';

const Profile = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const data = playerId
          ? await playerService.getPlayerProfile(playerId)
          : await playerService.getMyProfile();
        if (!cancelled) setProfile(data);
      } catch (error) {
        if (cancelled) return;
        if (!playerId && error.response?.status === 401) {
          navigate('/login');
          return;
        }
        if (playerId && error.response?.status === 404) {
          setNotFound(true);
        } else {
          toast({ title: 'Error', description: 'Failed to load profile', variant: 'destructive' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProfile();
    return () => { cancelled = true; };
  }, [playerId, navigate, toast]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (notFound || !profile) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md text-center">
            <CardContent className="pt-8">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
              <Link to="/"><Button>Return Home</Button></Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const { stats } = profile;

  const profileTitle = `${profile.name || profile.email} - BookQuiz Profile`;
  const profileDescription = `${stats.total_attempts} quizzes taken, ${stats.average_score}% average score on BookQuiz.`;

  const handleSignOut = () => {
    playerService.logout();
    navigate('/');
  };

  return (
    <Layout>
      <SEOHead
        title={profileTitle}
        description={profileDescription}
        url={playerId ? `/players/${playerId}` : '/profile'}
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 mb-8 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">{profile.name || profile.email}</h1>
            <p className="text-blue-100">{profile.email}</p>
          </div>
          {!playerId && (
            <Button
              variant="secondary"
              className="bg-white text-blue-700 hover:bg-blue-50 self-start md:self-center"
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card><CardContent className="pt-6 text-center">
            <BookOpen className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">{stats.total_attempts}</div>
            <div className="text-sm text-gray-600">Quizzes Taken</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <Target className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">{stats.average_score}%</div>
            <div className="text-sm text-gray-600">Average Score</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <Star className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">{stats.highest_score}%</div>
            <div className="text-sm text-gray-600">Highest Score</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <Calendar className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">{stats.streak}</div>
            <div className="text-sm text-gray-600">Day Streak</div>
          </CardContent></Card>
        </div>

        {profile.recent_activity.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Recent Activity</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {profile.recent_activity.map((attempt) => (
                  <div key={attempt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium">{attempt.quiz_title}</div>
                    <div className="font-bold text-blue-600">{attempt.score}%</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {profile.pinned_books && profile.pinned_books.length > 0 && (
          <Card className="mt-8">
            <CardHeader><CardTitle>Books You're Tracking</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {profile.pinned_books.map((pin) => (
                  <div key={pin.book_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">{pin.book_title}</div>
                      <div className="text-sm text-gray-600">
                        {pin.is_due
                          ? 'Due for review'
                          : `Next review in ${pin.days_until_due} day${pin.days_until_due === 1 ? '' : 's'}`}
                      </div>
                    </div>
                    {pin.quiz_id && (
                      <Link to={`/quiz/${pin.quiz_id}`}>
                        <Button size="sm" variant="outline">Review</Button>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/">
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <BookOpen className="h-4 w-4 mr-2" />
              Browse More Books
            </Button>
          </Link>
          <Link to="/leaderboard">
            <Button variant="outline">
              <Trophy className="h-4 w-4 mr-2" />
              View Leaderboard
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
